"use client"
import React, { useState, useEffect } from "react"
import { getAllCourses } from "../../../api/courseService"

interface PackageContentStepProps {
  data: any
  onChange: (data: any) => void
  formRef: React.RefObject<HTMLFormElement | null>
  onNext: () => void
  onBack: () => void
  onSaveDraft?: () => void
}

interface Offering {
  id: string
  title: string
  type: string
  category: string
  selected: boolean
}

const PackageContentStep: React.FC<PackageContentStepProps> = ({ 
  data, 
  onChange, 
  formRef,
  onNext,
  onBack,
  onSaveDraft
}) => {
  const [activeCategory, setActiveCategory] = useState("Courses")
  const [activeTab, setActiveTab] = useState<"selected" | "unselected">("unselected")
  const [searchQuery, setSearchQuery] = useState("")
  const [offerings, setOfferings] = useState<Offering[]>([])
  const [filteredOfferings, setFilteredOfferings] = useState<Offering[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Fetch courses from API
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoading(true)
        const response = await getAllCourses()
        
        if (response.status === 200 && response.data) {
          // Map API courses to Offering format
          const apiCourses: Offering[] = response.data.map((course: any) => ({
            id: course._id,
            title: course.title,
            type: "Course",
            category: "Courses",
            selected: data.courses ? data.courses.includes(course._id) : false
          }))

          // Telegram groups (empty for now)
          const telegramGroups: Offering[] = []

          setOfferings([...apiCourses, ...telegramGroups])
        } else {
          console.error("Failed to fetch courses:", response.message)
          setOfferings([])
        }
      } catch (error) {
        console.error("Error fetching courses:", error)
        setOfferings([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchCourses()
  }, [data.courses])

  // Filter offerings based on category, tab, and search query
  useEffect(() => {
    let result = offerings.filter(offering => 
      offering.category === activeCategory &&
      offering.title.toLowerCase().includes(searchQuery.toLowerCase())
    )

    if (activeTab === "selected") {
      result = result.filter(offering => offering.selected)
    } else {
      result = result.filter(offering => !offering.selected)
    }

    setFilteredOfferings(result)
  }, [offerings, activeCategory, activeTab, searchQuery])

  // Get counts for each category
  const categoryCounts = {
    "Courses": offerings.filter(o => o.category === "Courses").length,
    "Telegram groups": offerings.filter(o => o.category === "Telegram groups").length,
  }

  const selectedCount = offerings.filter(o => o.selected).length

  const toggleOfferingSelection = (id: string, category: string) => {
    const updatedOfferings = offerings.map(offering => 
      offering.id === id ? { ...offering, selected: !offering.selected } : offering
    )
    
    setOfferings(updatedOfferings)
    
    // Update parent component with selected offerings
    const selectedCourses = updatedOfferings
      .filter(o => o.selected && o.category === "Courses")
      .map(o => o.id)
    
    const selectedTelegramIds = updatedOfferings
      .filter(o => o.selected && o.category === "Telegram groups")
      .map(o => o.id)
    
    onChange({ 
      ...data, 
      courses: selectedCourses,
      telegramId: selectedTelegramIds
    })
  }

  const categories = [
    { name: "Courses", count: categoryCounts.Courses },
    { name: "Telegram groups", count: categoryCounts["Telegram groups"] },
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onNext()
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1.5">Add offerings to your packages</h2>
        <p className="text-gray-600 text-sm">
          Select and add multiple courses and telegram groups to your package.
        </p>
      </div>

      {/* Category Tabs */}
      <div className="overflow-x-auto">
        <div className="flex space-x-1.5 pb-1.5 min-w-max">
          {categories.map((category) => (
            <button
              key={category.name}
              type="button"
              onClick={() => setActiveCategory(category.name)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                activeCategory === category.name
                  ? "bg-purple-100 text-purple-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        />
        <svg
          className="absolute right-2.5 top-1.5 h-4 w-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Selected/Unselected Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab("selected")}
          className={`px-3 py-1.5 font-medium text-xs ${
            activeTab === "selected"
              ? "border-b-2 border-purple-500 text-purple-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Selected ({selectedCount})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("unselected")}
          className={`px-3 py-1.5 font-medium text-xs ${
            activeTab === "unselected"
              ? "border-b-2 border-purple-500 text-purple-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Unselected
        </button>
      </div>

      {/* Offerings List */}
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="text-center py-6 text-gray-500">
            <p className="text-sm">Loading offerings...</p>
          </div>
        ) : filteredOfferings.length > 0 ? (
          filteredOfferings.map((offering) => (
            <div
              key={offering.id}
              className="flex items-center p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={offering.selected}
                onChange={() => toggleOfferingSelection(offering.id, offering.category)}
                className="h-3.5 w-3.5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <div className="ml-2.5 flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">
                  {offering.title}
                </p>
                <p className="text-[10px] text-gray-500 capitalize">{offering.type}</p>
              </div>
              {offering.selected && (
                <span className="bg-purple-100 text-purple-800 text-[10px] font-medium px-2 py-0.5 rounded">
                  Selected
                </span>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-6 text-gray-500">
            <p className="text-sm">No {activeTab === "selected" ? "selected" : "available"} offerings found</p>
            <p className="text-xs mt-1">
              {activeTab === "selected" 
                ? "Select offerings from the Unselected tab" 
                : "No offerings available in this category"}
            </p>
          </div>
        )}
      </div>

      {/* Selection Summary */}
      <div className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
        <p className="text-xs font-medium text-gray-700">
          {selectedCount} offering{selectedCount !== 1 ? "s" : ""} selected
        </p>
        <div className="flex space-x-1.5">
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={() => {
                const updatedOfferings = offerings.map(o => ({ ...o, selected: false }))
                setOfferings(updatedOfferings)
                onChange({ ...data, courses: [], telegramId: [] })
              }}
              className="text-xs text-red-600 hover:text-red-800 font-medium"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

{/* Navigation Buttons */}
<div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-gray-200 space-y-3 sm:space-y-0">
  <button
    type="button"
    onClick={onBack}
    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-purple-500 hover:text-purple-600 transition-all duration-200 font-medium flex items-center space-x-1.5 text-sm"
  >
    <svg
      className="w-3 h-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
    <span>Back</span>
  </button>

  <div className="flex space-x-2">
    {onSaveDraft && (
      <button
        type="button"
        onClick={onSaveDraft}
        className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-purple-500 hover:text-purple-600 transition-all duration-200 font-medium flex items-center space-x-1.5 disabled:opacity-50 text-sm"
      >
        <span>Save as Draft</span>
      </button>
    )}

    <button
      type="submit"
      className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg font-semibold flex items-center space-x-1.5 text-sm"
    >
      <span>Continue</span>
      <svg
        className="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>
  </div>
</div>     
    </form>
  )
}

export default PackageContentStep